import { useEffect, useState } from 'react';
import { SearchIcon } from '@ninja/icons/web';
import { IFormSchema, mapMultiSelectDropDownField, mapTextField, useReactForm } from '@ninja/utilities';
import {
	GlobalAPIError,
	NetworkSearchFilterError,
	getNetworkSearchFilters,
} from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../../../../../contexts/app';
import { usePlanFacilityType } from './helpers/getFacilityTypes';
import { PlanCity, usePlanCountry } from './helpers/getPlanCountry';

export type NetworkSearchFilterForm = {
	facilityName?: string;
	facilityTypeIds?: number[];
	countryIds?: number[];
	cityIds?: number[];
};

export const useNetworkListForm = (planId: number) => {
	const { translate: t, apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const { getFacilityTypeFromPlan } = usePlanFacilityType();
	const { getCountryFromPlan } = usePlanCountry();
	const [planFiltersResponse, setPlanFiltersResponse] = useState<{ cities: PlanCity[]; facilityTypes: number[] }>();
	const [citiesByCountry, setCitiesByCountry] = useState<PlanCity[]>([]);
	const [isNetworkListNotAvailable, setIsNetworkListNotAvailable] = useState<boolean>();

	useEffect(() => {
		(async () => {
			try {
				const response = await getNetworkSearchFilters(apiConnectionOptions)({
					parameters: { planId },
					body: undefined,
				});

				setPlanFiltersResponse(response);
				setCitiesByCountry(response.cities);
				setIsNetworkListNotAvailable(false);
			} catch (errors) {
				const { mapped } = errors as NetworkSearchFilterError;

				if (mapped?.some((e) => e.code === 'NoNetworkAvailable')) {
					setPlanFiltersResponse(undefined);
					setCitiesByCountry([]);
					setIsNetworkListNotAvailable(true);
				} else {
					onGeneralErrorHandling(errors as GlobalAPIError);
				}
			}
		})();
	}, []);

	const getCitiesByCountry = (countryId: number[]) => {
		const filteredCities = planFiltersResponse?.cities?.filter((city) => countryId.includes(city.countryId)) ?? [];

		return filteredCities;
	};

	const form = useReactForm<NetworkSearchFilterForm>({
		initialValues: { countryIds: [236] },
		validateBehavior: 'on-blur',
	});

	const fields: IFormSchema<NetworkSearchFilterForm>['fields'] = [
		mapTextField({
			field: 'facilityName',
			label: t('quote-list:quote-benefits-modal.network-list-content.facility-name'),
			placeholder: t('quote-list:quote-benefits-modal.network-list-content.placeholder-search'),
			startIcon: SearchIcon,
			debounceTimeInMilliseconds: 500,
		}),
		getFacilityTypeFromPlan(planFiltersResponse?.facilityTypes ?? []),
		getCountryFromPlan(planFiltersResponse?.cities ?? []),
		mapMultiSelectDropDownField({
			field: 'cityIds',
			options: citiesByCountry,
			label: t('quote-list:quote-benefits-modal.network-list-content.city'),
			placeholder: t('quote-list:quote-benefits-modal.network-list-content.placeholder-select'),
			labelKey: 'name',
			valueKey: 'id',
			isSearchable: true,
			cancelButtonText: t('quote-list:quote-benefits-modal.network-list-content.cancel'),
			applyButtonText: t('quote-list:quote-benefits-modal.network-list-content.apply'),
		}),
	];

	const selectedCountry = form.getValues('countryIds');

	useEffect(() => {
		if (selectedCountry && selectedCountry?.length > 0) {
			const cities = getCitiesByCountry(selectedCountry);

			setCitiesByCountry(cities);
		} else {
			setCitiesByCountry(planFiltersResponse?.cities ?? []);
		}
	}, [selectedCountry]);

	return {
		fields,
		form,
		planCities: planFiltersResponse?.cities,
		isNetworkListNotAvailable,
	};
};
